import { createApi } from '@reduxjs/toolkit/query/react';
import { transformResponses } from 'src/app-redux/rtkq.interceptor';
import type { AssignVehicleAndDriversArgs, Booking, FetchAllBookingsArgs } from 'src/booking/Booking';
import { apiResources } from 'src/common/Constants';
import { filterObject } from 'src/common/utils/object.util';
import { getLoggedInUser, isShipperUser } from 'src/management/User';
import { baseQueryWithAuth } from '../rtkq.interceptor';

/** Returns the booking status update URL for a given booking and status. */
function statusURL(bookingId: number, status: number): string {
  return `${apiResources.booking}/${bookingId}/status/${status}/process`;
}

const bookingsTag = 'bookings';

/** RTK Query API slice for booking-related endpoints. */
export const bookingsApi = createApi({
  reducerPath: 'bookingsApi',
  tagTypes: [bookingsTag],
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    /** Query to fetch all bookings with filters and pagination. */
    fetchAllBookings: builder.query<Array<Booking>, FetchAllBookingsArgs>({
      providesTags: [bookingsTag],
      query: (args) => {
        const user = getLoggedInUser();
        const shipper = isShipperUser(user) ? `/shipper/${user.id}` : '';
        const paramsObject = {
          invoiced: args.invoiced,
          page: args.page - 1,
          pageSize: 4,
          filter: args.type,
          filterCreatedByMe: args.createdByMe,
          fromDate: args.startDate,
          toDate: args.endDate,
          goodsTypeId: args.goodsType,
          paymentModeTypeIds: args.paymentType,
          filterDriverAssigned: args.filterDriverAssigned ? true : undefined,
          ...args.searchParam,
        };
        const params = filterObject(paramsObject);
        return { url: `${apiResources.booking}${shipper}/v3`, params };
      },
      ...transformResponses,
    }),
    /** Query to fetch a single booking by booking ID. */
    fetchBooking: builder.query<Booking, number>({
      providesTags: [bookingsTag],
      query: (bookingID) => `${apiResources.booking}/${bookingID}/v3`,
      ...transformResponses,
    }),
    /** Mutation to assign a driver to a booking. */
    assignDriver: builder.mutation<string, { bookingId: number; driverId: number }>({
      query: ({ bookingId, driverId }) => ({
        url: `${apiResources.booking}/${bookingId}/driver/${driverId}/assign`,
        method: 'POST',
      }),
      ...transformResponses,
      invalidatesTags: (_result, error) => (error ? [] : [bookingsTag]),
    }),
    /** Mutation to reassign a driver and vehicle to a booking. */
    reAssignDriver: builder.mutation<
      string,
      { bookingId: number; toBeAssignedDriverId: number; toBeAssignedVehicleId: number }
    >({
      query: (body) => ({ url: `${apiResources.booking}/reassign`, method: 'POST', body }),
      ...transformResponses,
      invalidatesTags: (_result, error) => (error ? [] : [bookingsTag]),
    }),
    /** Mutation to assign multiple drivers and vehicles to a booking. */
    assignMultipleDriverAndVehicle: builder.mutation<string, AssignVehicleAndDriversArgs>({
      query: (body) => ({ url: `${apiResources.bid}/assign/v3`, method: 'POST', body }),
      ...transformResponses,
      invalidatesTags: (_result, error) => (error ? [] : [bookingsTag]),
    }),
    /** Mutation to update the status of a booking. */
    updateBookingStatus: builder.mutation<
      string,
      { bookingId: number; status: number; collectedAmount?: number; refusalReasonId?: number }
    >({
      query({ bookingId, status, ...body }) {
        const { expandedTripStatuses } = getLoggedInUser().preferences;
        return {
          url: statusURL(bookingId, status),
          method: 'POST',
          body: { ...body, expanded: expandedTripStatuses },
        };
      },
      ...transformResponses,
      invalidatesTags: (_result, error) => (error ? [] : [bookingsTag]),
    }),
    /** Mutation to cancel a booking with a refusal reason. */
    cancelBookingStatus: builder.mutation<string, { bookingId: number; refusalReasonId?: number }>({
      query({ bookingId, refusalReasonId }) {
        const user = getLoggedInUser();
        return {
          url: statusURL(bookingId, 13),
          method: 'POST',
          body: {
            distanceInKilometers: 0,
            timeInHours: 0,
            cancellationType: 1,
            refusalReasonId,
            cancellationReason: `Cancelled via portal by user ${user.id}`,
            expanded: user.preferences.expandedTripStatuses,
          },
        };
      },
      ...transformResponses,
      invalidatesTags: (_result, error) => (error ? [] : [bookingsTag]),
    }),
  }),
});

/** RTK Query hooks for booking-related endpoints. */
export const {
  useAssignDriverMutation,
  useReAssignDriverMutation,
  useFetchAllBookingsQuery,
  useFetchBookingQuery,
  useAssignMultipleDriverAndVehicleMutation,
  useUpdateBookingStatusMutation,
  useCancelBookingStatusMutation,
} = bookingsApi;
